import {
  Component,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';
import { MatError, MatHint } from '@angular/material/form-field';
import { FileValueAccessor } from '../../../../directives/file-value-accessor.directive';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { FileUtilsService } from '../../../../services/file-utils.service';
import { ConfigService } from '../../../services/config.service';
import { IFileExport } from '../../../../interfaces/IFileExport.interface';
import { IFileFieldProps } from '../../../interfaces/IFileFieldProps.interface';

@Component({
  selector: 'app-formly-field-file',
  standalone: true,
  templateUrl: './file-form.component.html',
  styleUrl: './file-form.component.scss',
  imports: [
    FormlyModule,
    ReactiveFormsModule,
    FileValueAccessor,
    MatButton,
    TranslatePipe,
    MatIcon,
    MatError,
    NgClass,
    MatHint,
    MatIconButton,
  ],
})
export class FileForm
  extends FieldType<FieldTypeConfig<IFileFieldProps>>
  implements OnInit
{
  private readonly _fileUtilsService: FileUtilsService =
    inject(FileUtilsService);
  private readonly _config = inject(ConfigService).fileUploadConfig;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  protected _base64Files: IFileExport[] = [];
  protected showDelete = signal(true);

  ngOnInit(): void {
    this.props.maxFiles = this.props.multiple
      ? (this.props.maxFiles ?? this._config.maxFileCount)
      : 1;
    this.props.maxFileSizeMB =
      this.props.maxFileSizeMB ?? this._config.maxFileSize;
    if (this.props.isHistory) {
      this.showOnlyFIleNames();
    } else {
      this._base64Files = (() => {
        try {
          return this.formControl.value
            ? JSON.parse(this.formControl.value)
            : [];
        } catch {
          return [];
        }
      })() as IFileExport[];
      if (this._base64Files.length === 0) {
        this.formControl.setValue(null);
      }
    }
  }

  public openFilePicker() {
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.click();
    }
  }

  public async onFileSelected(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;
    const max = this.props.maxFiles!;

    if (!target.files || target.files.length === 0) return;

    if (this._base64Files.length + target.files.length > max) {
      this.formControl.setErrors({ maxCount: true });
      this.formControl.markAsTouched();
      return;
    }

    const newBase64Files: IFileExport[] = [];

    for (const file of target.files) {
      if (this._checkFile(file)) {
        const base64 = await this._fileUtilsService.toBase64(file);
        newBase64Files.push({
          fileName: file.name,
          content_type: file.type,
          data: base64.split(',')[1],
        });
      } else {
        return;
      }
    }

    this._base64Files = [...this._base64Files, ...newBase64Files];
    this.formControl.setValue(JSON.stringify(this._base64Files));
    this.formControl.setErrors(null);
    this.props.disabled = this._base64Files.length >= max;
  }

  private _checkFile(file: File) {
    const acceptedTypes = this.props.accept?.split(',') || [];

    if (!this._fileUtilsService.checkFileType(file, acceptedTypes)) {
      this.formControl.setErrors({ fileType: true });
      this.formControl.markAsTouched();

      return false;
    }

    if (
      this._fileUtilsService.isFileTooLarge(file, this.props.maxFileSizeMB ?? 5)
    ) {
      this.formControl.setErrors({ fileSize: true });
      this.formControl.markAsTouched();

      return false;
    }

    if (file.size === 0) {
      this.formControl.setErrors({ sizeIs0: true });
      this.formControl.markAsTouched();

      return false;
    }
    return true;
  }

  deleteFile(file: IFileExport): void {
    const index = this._base64Files.indexOf(file);
    if (index > -1) {
      this._base64Files.splice(index, 1);
      this.formControl.setValue(JSON.stringify(this._base64Files));
      this.formControl.markAsDirty();
    }
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
    this.props.disabled =
      this._base64Files.length >=
      (this.props.maxFiles ?? this._config.maxFileCount);

    if (this._base64Files.length === 0) {
      this.formControl.setValue(null);
    }
  }

  private showOnlyFIleNames() {
    this.showDelete.set(false);
    try {
      const val = JSON.parse(this.formControl.value) as IFileExport[];
      val.forEach((item) => {
        this._base64Files.push({
          fileName: item.fileName,
          data: '',
          content_type: '',
        });
      });
    } catch {
      this._base64Files.push({
        fileName: 'Oops!!',
        data: '',
        content_type: '',
      });
    }
  }
}
